<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import * as L from "leaflet"
import "leaflet/dist/leaflet.css"
import "leaflet.markercluster/dist/MarkerCluster.css"
import "leaflet.markercluster/dist/MarkerCluster.Default.css"
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue"
import { useDebounceFn } from "@vueuse/core"
import defaultMarkerIconUrl from "@icons/building/marker.svg?url"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Propriétés attendues pour une Feature GeoJSON Point
 */
interface GeoJSONPointFeatureProperties {
    /**
     * Nom de la légende (pour l'affichage dans la légende de la carte)
     */
    legendName?: string
    /**
     * Icône de la légende (pour l'affichage dans la légende de la carte)
     */
    legendIcon?: string
    /**
     * URL de l'icône du marqueur
     */
    icon?: string
    /**
     * Contenu HTML personnalisé pour le marqueur
     */
    customHtml?: string
    /**
     * Taille de l'icône personnalisée [largeur, hauteur]
     */
    iconSize?: [number, number]
    /**
     * Nom du marqueur (utilisé pour le tooltip)
     */
    name?: string
    /**
     * Titre du marqueur (utilisé pour le tooltip, fallback si name n'est pas défini)
     */
    title?: string
    /**
     * Autres propriétés personnalisées
     */
    [key: string]: unknown
}

/**
 * Style visuel d'une parcelle
 */
type ParcelStyle = "default" | "selected"

/**
 * Feature GeoJSON de type Point
 */
/**
 * Feature GeoJSON de type Point
 *
 * Note sur l'ordre des coordonnées :
 * - GeoJSON spécifie [longitude, latitude] (lng, lat)
 * - Leaflet attend [latitude, longitude] (lat, lng)
 * - La conversion est effectuée lors de la création des marqueurs (ligne ~370)
 */
interface GeoJSONPointFeature {
    type: "Feature"
    geometry: {
        type: "Point"
        coordinates: [number, number] // [lng, lat] per GeoJSON spec
    }
    properties: GeoJSONPointFeatureProperties
}

/**
 * Propriétés attendues pour une Feature GeoJSON Polygon/MultiPolygon
 */
interface GeoJSONPolygonFeatureProperties {
    /**
     * Identifiant métier associé (ex: id parcelle/bâtiment)
     */
    id_building?: string
    /**
     * Style métier de la parcelle
     */
    parcelStyle?: ParcelStyle
    /**
     * Autres propriétés personnalisées
     */
    [key: string]: unknown
}

/**
 * Feature GeoJSON de type Polygon/MultiPolygon
 */
interface GeoJSONPolygonFeature {
    type: "Feature"
    geometry: {
        type: "Polygon" | "MultiPolygon"
        coordinates: unknown
    }
    properties: GeoJSONPolygonFeatureProperties
}

/**
 * Sous-ensemble des options Leaflet (PathOptions) passées via le GeoJSON (sérialisable).
 * Le composant parent est responsable du style métier ; ici on fusionne avec un défaut neutre.
 */
interface GeoJSONLinePathStyle {
    color?: string
    weight?: number
    opacity?: number
    dashArray?: string
}

/**
 * Feature GeoJSON de type LineString / MultiLineString
 */
interface GeoJSONLineFeatureProperties {
    /**
     * Style de tracé (fourni par le parent, ex. couche métier qui construit le GeoJSON).
     */
    pathStyle?: GeoJSONLinePathStyle
    [key: string]: unknown
}

interface GeoJSONLineFeature {
    type: "Feature"
    geometry: {
        type: "LineString" | "MultiLineString"
        coordinates: unknown
    }
    properties: GeoJSONLineFeatureProperties
}

type GeoJSONFeature =
    | GeoJSONPointFeature
    | GeoJSONPolygonFeature
    | GeoJSONLineFeature

function isPointFeature(
    feature: GeoJSONFeature
): feature is GeoJSONPointFeature {
    return feature.geometry.type === "Point"
}

function isLineFeature(feature: GeoJSONFeature): feature is GeoJSONLineFeature {
    return (
        feature.geometry.type === "LineString" ||
        feature.geometry.type === "MultiLineString"
    )
}

function isPolygonFeature(
    feature: GeoJSONFeature
): feature is GeoJSONPolygonFeature {
    return (
        feature.geometry.type === "Polygon" ||
        feature.geometry.type === "MultiPolygon"
    )
}

/**
 * FeatureCollection GeoJSON
 */
interface GeoJSONFeatureCollection {
    type: "FeatureCollection"
    features: GeoJSONFeature[]
}

/**
 * Bounds de la carte
 */
interface MapBounds {
    lat_min: number
    lon_min: number
    lat_max: number
    lon_max: number
}

/**
 * Type de fond de carte disponible
 */
type MapTileLayerType = "streets" | "satellite"

// ================================
// PROPS & EMITS
// ================================

type Props = {
    /**
     * Données GeoJSON à afficher sur la carte
     */
    geojson?: GeoJSONFeatureCollection
    /**
     * Position géographique du centre [latitude, longitude]
     */
    center?: [number, number]
    /**
     * Niveau de zoom par défaut
     */
    zoom?: number
    /**
     * Zoom minimum possible
     */
    minZoom?: number
    /**
     * Zoom maximum possible
     */
    maxZoom?: number
    /**
     * État de chargement de la carte
     */
    loading?: boolean
    /**
     * Message affiché en bas à gauche pendant le chargement
     */
    loadingMessage?: string
    /**
     * Si true, masque l'overlay visuel de loading
     */
    silent?: boolean
    /**
     * Active le clustering des marqueurs
     */
    clustering?: boolean
    /**
     * Fond affiché au chargement : `streets` (plan) ou `satellite` (vue aérienne).
     * Ignoré si {@link tileLayerUrl} est fourni (une seule couche personnalisée).
     */
    defaultTileLayerType?: MapTileLayerType
    /**
     * URL de la couche de tuiles (optionnel, utilise Mapbox par défaut)
     */
    tileLayerUrl?: string
    /**
     * URL de l'icône par défaut pour les marqueurs
     */
    defaultIconUrl?: string
    /**
     * Hauteur de la carte
     */
    height?: string
    /**
     * Si true, la carte s'ajuste automatiquement aux marqueurs lors de leur chargement.
     * Si false, la carte respecte les props center et zoom.
     * Par défaut: true
     */
    autoFitBounds?: boolean
    /**
     * Délai (ms) avant d’émettre `update:bounds` après déplacement / zoom de la carte.
     */
    boundsEmitDebounceMs?: number
}

const props = withDefaults(defineProps<Props>(), {
    geojson: undefined,
    center: () => [48.8566, 2.3522],
    zoom: 10,
    minZoom: undefined,
    maxZoom: undefined,
    loading: false,
    loadingMessage: "",
    silent: false,
    clustering: true,
    defaultTileLayerType: "streets",
    tileLayerUrl: undefined,
    defaultIconUrl: defaultMarkerIconUrl,
    height: "33.375rem",
    autoFitBounds: true,
    boundsEmitDebounceMs: 280,
})

const emit = defineEmits<{
    /**
     * Émis au clic sur un marqueur avec la Feature GeoJSON complète
     */
    "select:marker": [feature: GeoJSONPointFeature]
    /**
     * Émis au clic sur un polygone avec la Feature GeoJSON complète
     */
    "select:polygon": [feature: GeoJSONPolygonFeature]
    /**
     * Émis lors du changement de bounds (debounced 1s)
     */
    "update:bounds": [bounds: MapBounds]
}>()

// ================================
// ÉTAT
// ================================

/**
 * Conteneur de la carte Leaflet
 */
const mapContainer = ref<HTMLElement | null>(null)
/**
 * Instance de la carte Leaflet
 */
const map = ref<L.Map | null>(null)
/**
 * Cluster de marqueurs (créé après chargement du plugin)
 */
const markersCluster = ref<L.MarkerClusterGroup | null>(null)
/**
 * Liste des marqueurs
 */
const markers = ref<L.Marker[]>([])
/**
 * Couche GeoJSON des lignes (LineString / MultiLineString)
 */
const lineLayer = ref<L.GeoJSON | null>(null)
/**
 * Couche GeoJSON des polygones
 */
const polygonLayer = ref<L.GeoJSON | null>(null)
/**
 * Couche de tuiles actuellement affichée
 */
const tileLayer = ref<L.TileLayer | null>(null)
/**
 * Bounds actuelles de la carte
 */
const bounds = ref<MapBounds | null>(null)
/**
 * Fond de carte actif (plan/satellite)
 */
const activeTileLayerType = ref<MapTileLayerType>(props.defaultTileLayerType)
/**
 * Timeout pour le fix d'affichage de la carte
 */
let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * Génère la légende dynamiquement basée sur les marqueurs présents
 * Le parent doit fournir legendName et legendIcon dans les properties
 *
 * Note sur la déduplication : La légende utilise l'icône (legendIcon) comme clé unique.
 * Si plusieurs features ont la même icône mais des noms de légende différents,
 * seule la première occurrence sera conservée. Ce comportement est intentionnel
 * pour éviter les doublons dans la légende basés sur l'icône.
 */
const getDynamicLegends = computed(() => {
    if (!props.geojson || !props.geojson.features.length) return []

    const legendMap = new Map<string, { image: string; name: string }>()

    props.geojson.features.forEach((feature) => {
        if (!isPointFeature(feature)) return

        const legendName = feature.properties.legendName
        const legendIcon =
            feature.properties.legendIcon || feature.properties.icon

        if (!legendName || !legendIcon) return

        const legendKey = legendIcon
        if (!legendMap.has(legendKey)) {
            legendMap.set(legendKey, {
                image: legendIcon,
                name: legendName,
            })
        }
    })

    return Array.from(legendMap.values())
})

/**
 * Indique si le switch plan/satellite est disponible.
 * Si une URL custom est fournie via props, on conserve le comportement existant.
 */
const getCanToggleTileLayer = computed((): boolean => !props.tileLayerUrl)

/**
 * Retourne l'URL de tuiles selon le type de fond demandé.
 */
const getTileLayerUrl = (tileLayerType: MapTileLayerType): string => {
    if (props.tileLayerUrl) {
        return props.tileLayerUrl
    }
    const token =
        import.meta.env.VITE_MAPBOX_TOKEN ??
        "pk.eyJ1IjoicnlhbnBlbnRhbG9nIiwiYSI6ImNrZThpNWd3ZTF3cDEzN243aWRqYzFqbnIifQ.FyaPeyRDdCTblU6bbNYmDQ"
    if (!token) {
        console.warn(
            "[BaseMap] VITE_MAPBOX_TOKEN non défini, utilisation d'une URL par défaut"
        )
        return tileLayerType === "satellite"
            ? "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
    }
    const mapboxStyle =
        tileLayerType === "satellite" ? "satellite-v9" : "streets-v12"
    return `https://api.mapbox.com/styles/v1/mapbox/${mapboxStyle}/tiles/512/{z}/{x}/{y}?access_token=${token}`
}

/**
 * Options Leaflet pour la couche de tuiles.
 * Les tuiles Mapbox Static Tiles sont en 512×512 : Leaflet attend souvent du 256×256,
 * d’où {@link https://docs.mapbox.com/api/maps/static-tiles/ | tileSize 512 + zoomOffset -1}.
 * Les URLs en `/tiles/256/` restent en 256×256 sans offset.
 */
function getTileLayerOptionsForUrl(url: string): L.TileLayerOptions {
    const isMapboxRaster =
        url.includes("api.mapbox.com") &&
        url.includes("/styles/") &&
        url.includes("/tiles/")
    if (!isMapboxRaster) {
        return {}
    }
    if (url.includes("/tiles/256/")) {
        return { maxZoom: 22, maxNativeZoom: 22 }
    }
    return {
        tileSize: 512,
        zoomOffset: -1,
        maxZoom: 22,
        maxNativeZoom: 22,
    }
}

/**
 * Applique la couche de tuiles active sur la carte.
 */
function applyTileLayer(): void {
    if (!map.value) return

    if (tileLayer.value) {
        map.value.removeLayer(tileLayer.value as unknown as L.Layer)
    }

    const nextTileUrl = getTileLayerUrl(activeTileLayerType.value)
    tileLayer.value = L.tileLayer(
        nextTileUrl,
        getTileLayerOptionsForUrl(nextTileUrl)
    )
    ;(tileLayer.value as unknown as L.Layer).addTo(map.value as L.Map)
}

watch(
    () => props.defaultTileLayerType,
    (v) => {
        if (props.tileLayerUrl) return
        const next = v ?? "streets"
        if (activeTileLayerType.value === next) return
        activeTileLayerType.value = next
        if (map.value) {
            applyTileLayer()
        }
    }
)

/**
 * Crée un marqueur personnalisé avec divIcon pour les stations météo
 *
 * ⚠️ SÉCURITÉ : Cette fonction insère iconUrl directement dans du HTML sans sanitization.
 * Ne doit être utilisée qu'avec des URLs d'icônes provenant de sources de confiance
 * (imports statiques, constantes, ou données validées).
 * Ne jamais utiliser avec des données utilisateur non validées.
 */
const createCustomMarkerIcon = (iconUrl: string): L.DivIcon => {
    return L.divIcon({
        className: "custom-marker",
        html: `<img src="${iconUrl}" alt="Marker" style="width: 24px; height: 24px;" />`,
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        popupAnchor: [0, -24],
    })
}

/**
 * Crée un marqueur custom avec du contenu HTML personnalisé
 *
 * ⚠️ SÉCURITÉ : Cette fonction insère htmlContent directement dans du HTML sans sanitization.
 * Ne doit être utilisée qu'avec du contenu HTML provenant de sources de confiance
 * (construites en interne, validées, ou générées de manière sécurisée).
 * Ne jamais utiliser avec des données utilisateur non validées ou non sanitizées.
 */
const createCustomHTMLMarkerIcon = (
    htmlContent: string,
    size: [number, number] = [20, 20]
): L.DivIcon => {
    return L.divIcon({
        className: "custom-html-marker",
        html: htmlContent,
        iconSize: size,
        iconAnchor: [size[0] / 2, size[1] / 2],
        popupAnchor: [0, -size[1] / 2],
    })
}

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Initialise la carte Leaflet
 */
function initMap(): void {
    if (!mapContainer.value) return

    const leafletMap = L.map(mapContainer.value).setView(
        props.center,
        props.zoom
    )
    /** Masque le crédit « Leaflet » ; les attributions des fonds (tuiles) restent affichées. */
    leafletMap.attributionControl.setPrefix("")
    map.value = leafletMap

    if (props.minZoom !== undefined) {
        leafletMap.setMinZoom(props.minZoom)
    }
    if (props.maxZoom !== undefined) {
        leafletMap.setMaxZoom(props.maxZoom)
    }

    applyTileLayer()
}

/**
 * Fonction debounced pour émettre les bounds
 */
const debouncedEmitBounds = useDebounceFn(
    () => {
        if (bounds.value) {
            emit("update:bounds", bounds.value)
        }
    },
    () => props.boundsEmitDebounceMs
)

/**
 * Met à jour les bounds de la carte
 */
function updateBounds(): void {
    if (!map.value) return

    const mapBounds = map.value.getBounds()
    bounds.value = {
        lat_min: mapBounds.getSouth(),
        lon_min: mapBounds.getWest(),
        lat_max: mapBounds.getNorth(),
        lon_max: mapBounds.getEast(),
    }

    debouncedEmitBounds()
}

/**
 * Charge les marqueurs sur la carte
 * 1 - Supprime les anciens marqueurs
 * 2 - Ajoute les nouveaux marqueurs
 */
function loadMarkers(): void {
    if (!map.value) return

    // Supprime les anciens marqueurs
    if (markersCluster.value) {
        markersCluster.value.clearLayers()
    }
    if (lineLayer.value) {
        lineLayer.value.remove()
        lineLayer.value = null
    }
    if (polygonLayer.value) {
        polygonLayer.value.remove()
        polygonLayer.value = null
    }
    markers.value.forEach((marker) => marker.remove())
    markers.value = []

    // Ajoute les nouveaux marqueurs
    addMarkers()
}

/**
 * Ajoute les marqueurs sur la carte depuis le GeoJSON
 */
function addMarkers(): void {
    if (!map.value || !props.geojson) return

    // Stocke la référence de la carte pour TypeScript
    const leafletMap = map.value

    // Liste des positions des marqueurs pour centrer la carte
    const markerPositions: L.LatLngExpression[] = []
    // Liste des features polygonales pour affichage des parcelles
    const polygonFeatures: GeoJSONPolygonFeature[] = []
    /** Lignes — rendues après les polygones pour rester visibles au-dessus du remplissage */
    const lineFeatures: GeoJSONLineFeature[] = []

    // Pour chaque feature GeoJSON
    props.geojson.features.forEach((feature) => {
        if (isLineFeature(feature)) {
            lineFeatures.push(feature)
            return
        }
        if (isPolygonFeature(feature)) {
            polygonFeatures.push(feature)
            return
        }
        if (!isPointFeature(feature)) return

        const [lng, lat] = feature.geometry.coordinates

        // Vérifie si c'est un marqueur custom avec HTML personnalisé
        let icon: L.DivIcon | L.Icon

        if (feature.properties.customHtml) {
            // Marqueur custom avec HTML personnalisé
            const size: [number, number] =
                feature.properties.iconSize || ([24, 24] as [number, number])
            icon = createCustomHTMLMarkerIcon(
                feature.properties.customHtml,
                size
            )
        } else {
            // Le parent doit fournir l'icône dans properties.icon
            const iconUrl = feature.properties.icon || props.defaultIconUrl

            // Crée l'icône personnalisée avec divIcon
            icon = createCustomMarkerIcon(iconUrl)
        }

        // Crée le marqueur
        const marker = L.marker([lat, lng], { icon })

        // Ajoute le tooltip si disponible
        const tooltipTitle =
            feature.properties.name || feature.properties.title || ""

        if (tooltipTitle) {
            marker.bindTooltip(tooltipTitle, {
                className: "marker-tooltip",
                direction: "top",
                offset: [0, -30],
                opacity: 1,
            })
        }

        // Gestion du clic sur le marqueur
        marker.on("click", () => {
            emit("select:marker", feature)
        })

        // Ajoute le marqueur à la liste
        markers.value.push(marker)

        // Ajoute au cluster si activé
        if (props.clustering && markersCluster.value) {
            markersCluster.value.addLayer(marker)
        } else if (!props.clustering) {
            // Ajoute directement à la carte si clustering désactivé
            marker.addTo(leafletMap as L.Map)
        } else {
            // Fallback : si le clustering est activé mais pas encore initialisé,
            // on ajoute le marqueur directement à la carte pour éviter qu'il soit invisible
            marker.addTo(leafletMap as L.Map)
        }

        // Ajoute la position à la liste pour centrer la carte
        markerPositions.push([lat, lng])
    })

    // Ajoute le cluster à la carte si activé
    if (props.clustering && markersCluster.value) {
        // Note: Type assertion nécessaire car les types de leaflet.markercluster
        // ne déclarent pas explicitement que MarkerClusterGroup étend L.Layer,
        // bien que ce soit le cas à l'exécution
        const clusterLayer = markersCluster.value as unknown as L.Layer
        ;(leafletMap as L.Map).addLayer(clusterLayer)
    }

    /** Défaut si aucune `pathStyle` n’est fournie sur la feature */
    const defaultLinePathStyle: L.PathOptions = {
        color: "#3388ff",
        weight: 2,
        opacity: 0.9,
    }

    const getLinePathStyle = (feature: GeoJSONLineFeature): L.PathOptions => {
        const ps = feature.properties.pathStyle
        if (!ps || typeof ps !== "object") {
            return { ...defaultLinePathStyle }
        }
        return {
            ...defaultLinePathStyle,
            ...ps,
        }
    }

    // Ajoute les polygones de parcelles
    if (polygonFeatures.length > 0) {
        const getPolygonStyle = (
            feature: GeoJSONPolygonFeature
        ): L.PathOptions => {
            if (feature.properties.parcelStyle === "selected") {
                return {
                    color: "#69D4B1",
                    opacity: 1,
                    fillColor: "#69D4B1",
                    fillOpacity: 0.5,
                    weight: 2,
                }
            }
            return {
                color: "#e6edf1",
                opacity: 0.5,
                fillColor: "#e6edf1",
                fillOpacity: 0.3,
                weight: 2,
            }
        }
        const getPolygonHoverStyle = (
            feature: GeoJSONPolygonFeature
        ): L.PathOptions => {
            if (feature.properties.parcelStyle === "selected") {
                return getPolygonStyle(feature)
            }
            return {
                color: "rgba(208, 185, 138, 1.00)",
                opacity: 0.2,
                fillColor: "rgba(208, 185, 138, 1.00)",
                fillOpacity: 0.3,
                weight: 2,
            }
        }

        polygonLayer.value = L.geoJSON(
            {
                type: "FeatureCollection",
                features: polygonFeatures,
            } as GeoJSON.FeatureCollection,
            {
                style: (feature) =>
                    getPolygonStyle(feature as GeoJSONPolygonFeature),
                onEachFeature: (feature, layer) => {
                    const polygonFeature = feature as GeoJSONPolygonFeature
                    layer.on("mouseover", () => {
                        const pathLayer = layer as L.Path
                        pathLayer.setStyle(getPolygonHoverStyle(polygonFeature))
                    })
                    layer.on("mouseout", () => {
                        const pathLayer = layer as L.Path
                        pathLayer.setStyle(getPolygonStyle(polygonFeature))
                    })
                    layer.on("click", () => {
                        emit("select:polygon", polygonFeature)
                    })
                },
            }
        ).addTo(leafletMap as L.Map)
    }

    /**
     * Lignes (réseau, etc.) : ajoutées après les polygones pour rester au-dessus du remplissage.
     * Sinon les LineString passent sous la parcelle et sont invisibles.
     */
    if (lineFeatures.length > 0) {
        lineLayer.value = L.geoJSON(
            {
                type: "FeatureCollection",
                features: lineFeatures,
            } as GeoJSON.FeatureCollection,
            {
                style: (f) => getLinePathStyle(f as GeoJSONLineFeature),
            }
        ).addTo(leafletMap as L.Map)
        lineLayer.value.bringToFront()
    }

    // Centre la carte sur tous les marqueurs si autoFitBounds est activé
    if (props.autoFitBounds) {
        let mapBounds: L.LatLngBounds | null = null

        if (markerPositions.length > 0) {
            mapBounds = L.latLngBounds(markerPositions)
        }

        if (lineLayer.value) {
            const lineBounds = lineLayer.value.getBounds()
            if (lineBounds.isValid()) {
                mapBounds = mapBounds
                    ? mapBounds.extend(lineBounds)
                    : lineBounds
            }
        }

        if (polygonLayer.value) {
            const polygonBounds = polygonLayer.value.getBounds()
            if (polygonBounds.isValid()) {
                mapBounds = mapBounds
                    ? mapBounds.extend(polygonBounds)
                    : polygonBounds
            }
        }

        if (mapBounds && mapBounds.isValid()) {
            leafletMap.fitBounds(mapBounds)
        }
    }

    // Met à jour les bounds après ajout des marqueurs
    updateBounds()
}

// ================================
// INTERACTIONS
// ================================

/**
 * Initialise le clustering après chargement de la carte
 */
async function initClustering(): Promise<void> {
    if (!map.value || !props.clustering) return

    // Expose Leaflet sur window pour garantir que le plugin s'attache à la bonne instance,
    // en évitant d'écraser une éventuelle autre instance globale déjà présente.
    if (typeof window !== "undefined") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const winAny = window as any
        if (!winAny.L) {
            winAny.L = L
        } else if (winAny.L !== L) {
            console.warn(
                "[BaseMap] window.L existe déjà et ne correspond pas à l'instance importée de Leaflet ; utilisation de l'instance globale existante pour le clustering."
            )
        }
    }

    // Import dynamique du plugin APRÈS que Leaflet soit complètement chargé
    await import("leaflet.markercluster")

    // Utilise window.L pour garantir qu'on a l'instance augmentée par le plugin
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const LeafletGlobal = (window as any).L

    // Crée le cluster maintenant que le plugin est chargé
    if (LeafletGlobal.markerClusterGroup) {
        markersCluster.value = LeafletGlobal.markerClusterGroup({
            maxClusterRadius: 40,
            disableClusteringAtZoom: 16,
        })

        // Charge les marqueurs
        loadMarkers()
    } else {
        console.error(
            "[BaseMap] markerClusterGroup non disponible après import."
        )
    }
}

/**
 * Gère l'événement 'load' de la carte
 */
function onMapLoad(): void {
    if (props.clustering) {
        initClustering()
    } else {
        loadMarkers()
    }
}

/**
 * Bascule entre fond plan et fond satellite.
 */
function onToggleTileLayer(): void {
    if (!getCanToggleTileLayer.value) return

    activeTileLayerType.value =
        activeTileLayerType.value === "streets" ? "satellite" : "streets"
    applyTileLayer()
}

/**
 * Recentre la carte sur la position définie par les props center et zoom.
 */
function onRecenterMap(): void {
    if (!map.value) return

    map.value.setView(props.center, props.zoom)
}

// ================================
// WATCHERS
// ================================

/**
 * Recharge les marqueurs quand le GeoJSON change
 */
watch(
    () => props.geojson,
    () => {
        if (map.value) {
            loadMarkers()
        }
    },
    { deep: true }
)

/**
 * Met à jour les bounds quand la carte bouge
 */
watch(
    () => map.value,
    (newMap, _oldMap, onCleanup) => {
        if (newMap) {
            newMap.on("moveend", updateBounds)
            newMap.on("zoomend", updateBounds)
            onCleanup(() => {
                newMap.off("moveend", updateBounds)
                newMap.off("zoomend", updateBounds)
            })
        }
    }
)

watch(
    () => props.center,
    (newCenter) => {
        if (map.value) {
            map.value.setView(newCenter, props.zoom)
        }
    },
    {
        immediate: true,
        deep: true,
    }
)

// ================================
// LIFECYCLE
// ================================

onMounted(() => {
    // Initialise la carte
    initMap()

    if (!map.value) return

    // Écoute l'événement 'load' de la carte pour garantir qu'elle est prête
    map.value.on("load", onMapLoad)

    // Force le déclenchement de l'événement 'load' si la carte est déjà prête
    // (cas où les tiles sont déjà en cache)
    map.value.whenReady(() => {
        if (!markersCluster.value && !markers.value.length) {
            map.value?.fire("load")
        }
    })

    // Fix pour éviter les problèmes d'affichage de zone grise quand l'app est en prod :
    // on attend que le conteneur soit complètement rendu avant de recalculer la taille de la carte.
    resizeTimeoutId = setTimeout(() => {
        map.value?.invalidateSize()
    }, 250)
})

/**
 * Nettoie les ressources lors du démontage du composant
 */
onBeforeUnmount(() => {
    // Annule le timeout s'il est encore en attente
    if (resizeTimeoutId !== null) {
        clearTimeout(resizeTimeoutId)
        resizeTimeoutId = null
    }

    // Supprime tous les marqueurs
    if (markersCluster.value) {
        markersCluster.value.clearLayers()
        markersCluster.value = null
    }
    markers.value.forEach((marker) => marker.remove())
    markers.value = []

    // Supprime les event listeners et détruit la carte
    if (map.value) {
        map.value.off("load", onMapLoad)
        map.value.off("moveend", updateBounds)
        map.value.off("zoomend", updateBounds)
        map.value.remove()
        map.value = null
    }
    tileLayer.value = null
})
</script>

<template>
    <div class="relative" :style="{ height: height }">
        <!-- ================================ -->
        <!-- ÉTAT DE CHARGEMENT -->
        <!-- ================================ -->
        <div
            v-if="loading && !silent"
            class="base-map__loading absolute inset-0 z-[1000] flex items-center justify-center bg-neutral-500/40"
        >
            <BaseProgressSpinner
                style="width: 3rem; height: 3rem"
                stroke-width="4"
                aria-label="Chargement de la carte"
            />
        </div>
        <div
            v-if="loading && loadingMessage"
            class="base-map__loading-message pointer-events-none absolute bottom-3 left-3 z-[1001] rounded-md bg-white/95 px-3 py-2 text-xs text-primary shadow-sm border border-neutral-200"
        >
            {{ loadingMessage }}
        </div>

        <!-- ================================ -->
        <!-- CARTE LEAFLET -->
        <!-- ================================ -->
        <div ref="mapContainer" class="relative h-full w-full"></div>
        <!-- Contenu superposé (actions, icônes) — pointer-events sur l’enfant -->
        <div
            class="pointer-events-none absolute inset-0 z-[550] flex items-start justify-end p-3"
        >
            <div class="pointer-events-auto flex flex-col items-end gap-2">
                <slot name="overlay" />
            </div>
        </div>
        <div
            class="absolute bottom-3 right-3 z-600 flex flex-col items-end gap-2"
        >
            <button
                type="button"
                class="cursor-pointer rounded-md border border-neutral-200 bg-white/95 px-3 py-2 text-xs font-medium text-primary shadow-sm hover:bg-white"
                aria-label="Recentrer la carte sur le centre défini"
                @click="onRecenterMap"
            >
                Recentrer
            </button>
            <button
                v-if="getCanToggleTileLayer"
                type="button"
                class="cursor-pointer rounded-md border border-neutral-200 bg-white/95 px-3 py-2 text-xs font-medium text-primary shadow-sm hover:bg-white"
                :aria-label="
                    activeTileLayerType === 'streets'
                        ? 'Basculer en vue satellite'
                        : 'Basculer en carte plan'
                "
                @click="onToggleTileLayer"
            >
                {{
                    activeTileLayerType === "streets"
                        ? "Vue satellite"
                        : "Carte 2D"
                }}
            </button>
        </div>

        <!-- ================================ -->
        <!-- LÉGENDES -->
        <!-- ================================ -->
        <div
            v-if="getDynamicLegends.length > 0"
            class="base-map__legend absolute bottom-0 left-0 right-0 z-[1000] bg-white/90 p-4"
        >
            <ul
                class="base-map__legend-list flex flex-row gap-4 justify-center items-center"
            >
                <li
                    v-for="(legend, index) in getDynamicLegends"
                    :key="index"
                    class="base-map__legend-item flex items-center text-sm"
                >
                    <img
                        class="base-map__legend-icon w-5 h-5 mr-3"
                        :src="legend.image"
                        :alt="legend.name"
                    />
                    {{ legend.name }}
                </li>
            </ul>
        </div>
    </div>
</template>

<style>
/* ================================ */
/* STYLES POUR LES MARQUEURS PERSONNALISÉS */
/* ================================ */
.custom-marker {
    background-color: transparent;
    border: none;
}

.custom-marker img {
    display: block;
    pointer-events: none;
}

.custom-html-marker {
    background-color: transparent;
    border: none;
}

/* ================================ */
/* STYLES GLOBAUX POUR LES TOOLTIPS */
/* ================================ */
.marker-tooltip {
    font-weight: 500;
}
</style>
